/**
 * <MyListingsTab />
 *
 * Seller inventory management.  Lists every product owned by the signed-in
 * user (all statuses) and lets them:
 *   * Edit price, condition, stock
 *   * Pause (status -> INACTIVE)
 *   * Resume (status -> ACTIVE)
 *
 * Backs onto:
 *   GET    /api/products/mine           -- full inventory
 *   PATCH  /api/products/{id}           -- edit or toggle status
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { Pencil, Pause, Play, RefreshCcw } from "lucide-react";
import { api, extractApiError, parseApiError } from "../../../lib/api";
import type { Product, ProductCondition, ProductStatus } from "../../../lib/types";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { ProductImage } from "../../../components/ui/ProductImage";
import { PRODUCT_CONDITIONS } from "../../../lib/conditions";
import { validatePrice, validateStockQuantity, validateTitle } from "../../../lib/productValidation";

const EDIT_FIELD_ORDER = ["title", "price", "stock_quantity"] as const;
type EditField = (typeof EDIT_FIELD_ORDER)[number];

export function MyListingsTab() {
  const [items, setItems] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Product[]>("/products/mine");
      setItems(r.data);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const replace = (p: Product) =>
    setItems((prev) => prev.map((x) => (x.id === p.id ? p : x)));

  return (
    <Card elevated data-testid="my-listings-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold">My listings</h2>
          <p className="text-ink-muted text-sm mt-1">
            Edit pricing, pause listings, or resume them when you're ready to sell again.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            loading={loading}
            leadingIcon={<RefreshCcw className="h-4 w-4" />}
            data-testid="my-listings-refresh"
          >
            Refresh
          </Button>
          <Link to="/sell">
            <Button variant="primary" size="sm" data-testid="my-listings-new">
              + New listing
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <p
            data-testid="my-listings-error"
            className="mb-3 text-sm font-semibold text-brand"
          >
            {error}
          </p>
        )}
        {items.length === 0 ? (
          <p
            data-testid="my-listings-empty"
            className="text-ink-muted text-center py-8"
          >
            {loading ? "Loading…" : "No listings yet. Click “New listing” to post your first part."}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-border">
            {items.map((p) => (
              <ListingRow key={p.id} product={p} onChange={replace} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function statusVariant(s: ProductStatus): "brand" | "solid" | "outline" | "soft" {
  if (s === "INACTIVE") return "outline";
  if (s === "SOLD") return "soft";
  return "solid"; // ACTIVE
}

function ListingRow({
  product,
  onChange,
}: {
  product: Product;
  onChange: (p: Product) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [form, setForm] = React.useState({
    price: String(product.price),
    condition: product.condition as ProductCondition,
    stock_quantity: String(product.stock_quantity),
    title: product.title,
  });
  const fieldRefs = React.useRef<Partial<Record<EditField, HTMLElement | null>>>({});

  const validateOne = React.useCallback(
    (key: EditField): string | undefined => {
      switch (key) {
        case "title":
          return validateTitle(form.title);
        case "price":
          return validatePrice(form.price);
        case "stock_quantity":
          return validateStockQuantity(form.stock_quantity);
        default:
          return undefined;
      }
    },
    [form.title, form.price, form.stock_quantity],
  );

  const handleBlur = (key: EditField) => () => {
    const msg = validateOne(key);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const focusField = (key: string) => {
    const el = fieldRefs.current[key as EditField];
    el?.focus();
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const togglePause = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = product.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const r = await api.patch<Product>(`/products/${product.id}`, { status: next });
      onChange(r.data);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors: Record<string, string> = {};
    for (const key of EDIT_FIELD_ORDER) {
      const msg = validateOne(key);
      if (msg) errors[key] = msg;
    }
    setFieldErrors(errors);
    const firstInvalid = EDIT_FIELD_ORDER.find((k) => errors[k]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    setBusy(true);
    try {
      const body: any = {
        title: form.title.trim(),
        price: Number(form.price),
        condition: form.condition,
        stock_quantity: Number(form.stock_quantity),
      };
      const r = await api.patch<Product>(`/products/${product.id}`, body);
      onChange(r.data);
      setEditing(false);
    } catch (err) {
      const parsed = parseApiError(err);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
        const firstServerInvalid = EDIT_FIELD_ORDER.find((k) => parsed.fieldErrors[k]);
        if (firstServerInvalid) focusField(firstServerInvalid);
      } else {
        setError(parsed.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const hero = product.images?.[0];

  return (
    <li className="py-4" data-testid={`listing-row-${product.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-16 w-16 rounded-lg overflow-hidden bg-surface-sunken border border-surface-border flex-shrink-0">
          <ProductImage
            src={hero}
            alt=""
            className="h-full w-full object-cover"
            placeholderClassName="h-full w-full"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/product/${product.id}`}
              className="font-semibold text-ink hover:text-brand truncate"
            >
              {product.title}
            </Link>
            <Badge variant={statusVariant(product.status)} size="sm">
              {product.status}
            </Badge>
          </div>
          <div className="text-xs text-ink-muted font-mono mt-1">
            {product.part_number} · {product.condition} · {product.stock_quantity} in stock
          </div>
          <div className="font-display text-lg font-black mt-1">
            ${product.price.toFixed(2)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditing((e) => !e);
              setFieldErrors({});
              setError(null);
            }}
            leadingIcon={<Pencil className="h-4 w-4" />}
            data-testid={`listing-edit-toggle-${product.id}`}
          >
            {editing ? "Close" : "Edit"}
          </Button>
          {product.status !== "SOLD" && (
            <Button
              variant={product.status === "ACTIVE" ? "outline" : "primary"}
              size="sm"
              loading={busy && !editing}
              onClick={togglePause}
              leadingIcon={
                product.status === "ACTIVE" ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )
              }
              data-testid={`listing-toggle-pause-${product.id}`}
            >
              {product.status === "ACTIVE" ? "Pause" : "Resume"}
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <form
          onSubmit={saveEdit}
          className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 rounded-xl border-2 border-surface-border bg-surface-raised p-4"
          data-testid={`listing-edit-form-${product.id}`}
        >
          <div className="md:col-span-4">
            <Input
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              onBlur={handleBlur("title")}
              error={fieldErrors.title}
              ref={(el) => {
                fieldRefs.current.title = el;
              }}
              data-testid={`listing-edit-title-${product.id}`}
            />
          </div>
          <Input
            label="Price (USD)"
            type="number"
            step="0.01"
            min="0"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            onBlur={handleBlur("price")}
            error={fieldErrors.price}
            ref={(el) => {
              fieldRefs.current.price = el;
            }}
            data-testid={`listing-edit-price-${product.id}`}
          />
          <Input
            label="Stock"
            type="number"
            min="0"
            value={form.stock_quantity}
            onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })}
            onBlur={handleBlur("stock_quantity")}
            error={fieldErrors.stock_quantity}
            ref={(el) => {
              fieldRefs.current.stock_quantity = el;
            }}
            data-testid={`listing-edit-stock-${product.id}`}
          />
          <Select
            label="Condition"
            value={form.condition}
            onChange={(e) => setForm({ ...form, condition: e.target.value as ProductCondition })}
            options={PRODUCT_CONDITIONS.map((c: { value: string; label: string }) => ({
              value: c.value,
              label: c.label,
            }))}
            data-testid={`listing-edit-condition-${product.id}`}
          />
          <div className="flex items-end">
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={busy}
              fullWidth
              data-testid={`listing-edit-save-${product.id}`}
            >
              Save
            </Button>
          </div>
          {error && (
            <p
              data-testid={`listing-edit-error-${product.id}`}
              className="md:col-span-4 text-sm font-semibold text-brand"
            >
              {error}
            </p>
          )}
        </form>
      )}
    </li>
  );
}
